import { IAntibodyScreeningResult } from '@nkr/models/index';
import { Button, Modal } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from 'ag-grid-community';
import { GridWrapper } from './styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    show: boolean,
    recipientAlias: string,
    data: IAntibodyScreeningResult[],
    processAntibodyScreeningOverride: (screeningId: number) => void,
    onClose: () => void
}

const AntibodyScreeningDetails = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendererd = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const { show, recipientAlias, data } = props;

    const columns: ColDef[] = [
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Serological</SortableHeaderComponent>,
            colId: 'serological',
            field: 'serological',
            width: 125,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Allele</SortableHeaderComponent>,
            colId: 'allele',
            field: 'allele',
            width: 130,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>
                    <div style={{ width: '100%' }}>
                        <div style={{ borderBottom: '1px solid #ccc' }}>
                            MFI
                        </div>
                        <div>
                            NML / CNT / RAW / RXN
                        </div>
                    </div>
                </SortableHeaderComponent>,
            colId: 'Nml / Cnt / Raw / Rxn [MFI]',
            // field: 'serological',
            width: 205,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true,
            valueGetter: (params: ValueGetterParams) => {
                let item = params.data as IAntibodyScreeningResult;
                return item.normalMfi;
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let item = params.data as IAntibodyScreeningResult;
                return <>{item.normalMfi} / {item.countMfi} / {item.rawMfi} / {item.rxnMfi}</>;
            }
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Bead</SortableHeaderComponent>,
            colId: 'bead',
            field: 'bead',
            width: 70,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor<br />Allele</SortableHeaderComponent>,
            colId: 'isDonorAllele',
            field: 'isDonorAllele',
            width: 80,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => params.value ? 'Yes' : 'No'
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>DSA</SortableHeaderComponent>,
            colId: 'isDsa',
            field: 'isDsa',
            width: 70,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => params.value ? 'Yes' : 'No'
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>De Novo DSA<br />This Sample</SortableHeaderComponent>,
            colId: 'isDeNovoDsa',
            field: 'isDeNovoDsa',
            width: 120,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => params.value ? 'Yes' : 'No'
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Overridden</SortableHeaderComponent>,
            colId: 'override',
            field: 'override',
            width: 110,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => params.value ? 'Yes' : 'No'
        },
        {
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Process<br />Override</SortableHeaderComponent>,
            colId: 'procOverride',
            width: 110,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            sortable: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IAntibodyScreeningResult;
                let { isDeNovoDsa, id } = data;
                return (isDeNovoDsa ? <button className='btn-xs btn-primary' onClick={() => props.processAntibodyScreeningOverride(id)}>Override</button> : <span>N/A</span>);
            },
        },
    ];

    const list = data != null ? data : [];
    
    return (
        <Modal bsSize='lg' show={show} backdrop='static' onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    Antibody Screening Results for {recipientAlias}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 5 }}>
                    <div style={{ marginRight: 10 }}>Legend:</div>
                    <div style={{ backgroundColor: '#ff6961', color: 'white', marginRight: 10, padding: 3, borderRadius: 4 }}>De Novo DSA Detected</div>
                    <div style={{ backgroundColor: 'lightyellow', marginRight: 10, padding: 3, borderRadius: 4 }}>DSA Detected</div>
                    <div style={{ backgroundColor: 'lightgreen', marginRight: 10, padding: 3, borderRadius: 4 }}>No DSA Detected</div>
                    <div style={{ backgroundColor: 'orange', padding: 3, borderRadius: 4 }}>DSA Overridden</div>
                </div>
                <GridWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        columnDefs={columns}
                        rowData={list}
                        headerHeight={42}
                        rowHeight={33}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        noRowsOverlayComponentFramework={() => <div></div>}
                        onGridReady={onGridReady}
                        onFirstDataRendered={onFirstDataRendererd}
                        getRowStyle={(params: any) => {
                            if (params.data.isDeNovoDsa == true && params.data.override == true) {
                                return { background: 'orange' };
                            }
                            if (params.data.isDeNovoDsa == true) {
                                return { background: '#ff6961' };
                            }
                            if (params.data.isDsa == true) {
                                return { background: 'lightyellow' };
                            }
                            if (params.data.isDonorAllele == true) {
                                return { background: 'lightgreen' };
                            }
                        }}
                    />
                </GridWrapper>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' onClick={props.onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default AntibodyScreeningDetails;

